import { ageGroup1, ageGroup2, ageGroup3, ageGroup4 } from '@/assets/messages';
import { AnnotatedType } from '@/components/annotated-type';
import { HorizontalBarChart } from '@/components/bar-chart/horizontal';
import type { BarChartItem } from '@/components/bar-chart/vertical';
import { StoryQuestion, useStoryQuestion } from '@/components/microstory/question';
import {
  OnStepEnterExitArgs,
  ScrollGraphic,
  ScrollRoot,
  ScrollStep,
  ScrollSteps,
} from '@/components/microstory/scroll';
import { StoryText } from '@/components/microstory/text';
import { SurveyBackground2023 } from '@/components/survey-background-2023';
import { getAgeGroupLabel, MediaType } from '@/pages/stories/go-to-sources';
import { useNumberFormatting } from '@/utils/use-number-formatting';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { useCallback, useState } from 'react';

export function GoToSources2023Chapter1({
  traditionalData,
  digitalData,
}: {
  traditionalData: GoToSourcesData['use_traditional_media'];
  digitalData: GoToSourcesData['use_digital_sources'];
}) {
  const { i18n } = useLingui();
  const percentFormat = useNumberFormatting('percent');

  const answers = [
    {
      id: MediaType.digital,
      label: t({
        message: `Digital sources such as social media and online news`,
        id: 'go-to-sources-2023.story-question.answer-1',
      }),
    },
    {
      id: MediaType.analog,
      label: t({
        message: `Traditional sources such as radio, TV and newspapers`,
        id: 'go-to-sources-2023.story-question.answer-2',
      }),
    },
  ];

  const { selectedAnswer, handleAnswer } = useStoryQuestion<(typeof answers)[number]>();

  function getSelectedAnswer(): keyof AgeGroups {
    return selectedAnswer && selectedAnswer.id === MediaType.digital ? '15_24' : '65plus';
  }

  const [digitalMediaChartVisible, setDigitalMediaChartVisible] = useState(false);
  const [digitalMediaChartData, setDigitalMediaChartData] = useState<BarChartItem[]>([]);

  const [analogMediaChartVisible, setAnalogMediaChartVisible] = useState(false);
  const [analogMediaChartData, setAnalogMediaChartData] = useState<BarChartItem[]>([]);

  const handleDigitalChartStepEnter = useCallback((e: OnStepEnterExitArgs<BarChartItem[]>) => {
    setDigitalMediaChartVisible(true);
    if (e.data) setDigitalMediaChartData(e.data);
  }, []);

  const handleAnalogChartStepEnter = useCallback((e: OnStepEnterExitArgs<BarChartItem[]>) => {
    setAnalogMediaChartVisible(true);
    if (e.data) setAnalogMediaChartData(e.data);
  }, []);

  return (
    <>
      <SurveyBackground2023 />

      <StoryQuestion
        question={t({
          message: `What's your go-to source for news and information?`,
          id: 'go-to-sources.story-question-2023.question',
        })}
        answerHeading={t({
          message: `I mostly use:`,
          id: 'go-to-sources.story-question-2023.answer-heading',
        })}
        answers={answers}
        selectedAnswer={selectedAnswer}
        response={t({
          message: `You're in line with most {generation}`,
          id: 'go-to-sources.story-question-2023.response',
          values: {
            generation: getAgeGroupLabel(i18n, getSelectedAnswer()).toLowerCase(),
          },
        })}
        onAnswer={handleAnswer}
      />

      <ScrollRoot>
        <ScrollGraphic>
          {selectedAnswer && selectedAnswer.id === 'analog' && (
            <HorizontalBarChart
              label={t({
                id: 'go-to-sources-2023.analog-chart-label',
                message: '% who mostly use traditional sources',
              })}
              data={analogMediaChartData}
              visible={analogMediaChartVisible}
              formatValue={percentFormat}
            />
          )}
          {selectedAnswer && selectedAnswer.id === 'digital' && (
            <HorizontalBarChart
              label={t({
                id: 'go-to-sources-2023.digital-chart-label',
                message: '% who mostly use digital sources',
              })}
              data={digitalMediaChartData}
              visible={digitalMediaChartVisible}
              formatValue={percentFormat}
            />
          )}
        </ScrollGraphic>

        {selectedAnswer && selectedAnswer.id === 'analog' && (
          <ScrollSteps onStepEnter={handleAnalogChartStepEnter} offset={0.5}>
            <ScrollStep
              data={[
                {
                  label: ageGroup4(i18n),
                  value: traditionalData['65plus'],
                },
              ]}
            >
              <StoryText>
                <Trans id="go-to-sources-2023.analog-chart.text-1">
                  On average, 75% of those aged <AnnotatedType>65+</AnnotatedType> use primarily
                  traditional sources…
                </Trans>
              </StoryText>
            </ScrollStep>

            <ScrollStep
              data={[
                {
                  label: ageGroup1(i18n),
                  value: traditionalData['15_24'],
                },
                {
                  label: ageGroup2(i18n),
                  value: traditionalData['25_39'],
                },
                {
                  label: ageGroup3(i18n),
                  value: traditionalData['40_64'],
                },
                {
                  label: ageGroup4(i18n),
                  value: traditionalData['65plus'],
                },
              ]}
            >
              <StoryText>
                <Trans id="go-to-sources-2023.analog-chart.text-2">
                  …compared to just 19% of <AnnotatedType>15–24-year-olds</AnnotatedType>
                </Trans>
              </StoryText>
            </ScrollStep>
          </ScrollSteps>
        )}

        {selectedAnswer && selectedAnswer.id === 'digital' && (
          <ScrollSteps onStepEnter={handleDigitalChartStepEnter} offset={0.5}>
            <ScrollStep
              data={[
                {
                  label: ageGroup1(i18n),
                  value: digitalData['15_24'],
                },
              ]}
            >
              <StoryText>
                <Trans id="go-to-sources-2023.digital-chart.text-1">
                  On average, 66% of <AnnotatedType>15–24-year-olds</AnnotatedType> use primarily
                  digital sources…
                </Trans>
              </StoryText>
            </ScrollStep>

            <ScrollStep
              data={[
                {
                  label: ageGroup1(i18n),
                  value: digitalData['15_24'],
                },
                {
                  label: ageGroup2(i18n),
                  value: digitalData['25_39'],
                },
                {
                  label: ageGroup3(i18n),
                  value: digitalData['40_64'],
                },
                {
                  label: ageGroup4(i18n),
                  value: digitalData['65plus'],
                },
              ]}
            >
              <StoryText>
                <Trans id="go-to-sources-2023.digital-chart.text-2">
                  …compared to just 11% of those aged <AnnotatedType>65+</AnnotatedType>
                </Trans>
              </StoryText>
            </ScrollStep>
          </ScrollSteps>
        )}
      </ScrollRoot>
    </>
  );
}
